import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks';
import {
  useFetchBlockedProducts,
  useUpdateBlockedProduct,
} from 'hooks/api/blocked-products';
import { useCreateRedirectedProducts } from 'hooks/api/redirected-products';
import { useCreatePrioritizedProducts } from 'hooks/api/prioritized-products';
import { useBlockedProductsSearchParams } from 'hooks/store/blocked-products';
import { useOffsetTop } from 'hooks/ui';
import { Scaffold } from 'layouts';
import { notify } from 'services';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import { DataTable, type DataTableRef } from 'components/ui/DataTable';

import { TabbedNavigation, Indicators } from '../shared-components';
import { useBlockedProductColumns } from './hooks';
import {
  BlockedProductsFilter,
  UpdateProductReasonDrawer,
  type UpdateProductReasonDrawerRef,
  CreatePrioritizedProductsDrawer,
  type CreatePrioritizedProductsDrawerRef,
  CreateRedirectedProductsDrawer,
  type CreateRedirectedProductsDrawerRef,
  DataTableDropdownMenu,
} from './components';

const NAVIGATE_TO_REDIRECTED_PRODUCTS_PAGE_ON_SUCCESS = false;
const NAVIGATE_TO_PRIORITIZED_PRODUCTS_PAGE_ON_SUCCESS = false;

export const BlockedProductsPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { filters, updateFilters, clearState } = useBlockedProductsSearchParams();
  const { fetchBlockedProducts } = useFetchBlockedProducts();
  const { updateProduct } = useUpdateBlockedProduct();
  const { createRedirectedProducts } = useCreateRedirectedProducts();
  const { createPrioritizedProducts } = useCreatePrioritizedProducts();

  const datatableWrapperRef = React.useRef<HTMLDivElement>(null);
  const updateProductReasonDrawerRef = React.useRef<UpdateProductReasonDrawerRef>(null);
  const createPrioritizedProductsDrawerRef = React.useRef<CreatePrioritizedProductsDrawerRef>(null);
  const createRedirectedProductsDrawerRef = React.useRef<CreateRedirectedProductsDrawerRef>(null);
  const dataTableRef = React.useRef<DataTableRef>(null);

  const datatableWrapperOffsetTop = useOffsetTop({
    ref: datatableWrapperRef,
    updateInterval: 1000,
  });

  const datatableWrapperStyle = React.useMemo((): React.CSSProperties => {
    return {
      minHeight: '100px',
      maxHeight: `calc(100vh - ${datatableWrapperOffsetTop + 150}px)`,
    };
  }, [datatableWrapperOffsetTop]);

  const isAdminOrPlanner = React.useMemo(() => {
    if (!profile?.type) return false;

    return ['admin', 'planner'].includes(profile.type);
  }, [profile?.type]);

  const columns = useBlockedProductColumns({
    onUpdateReason: updateProductReasonDrawerRef.current?.open,
    showSelectionColumn: isAdminOrPlanner,
    showActionsDropdown: isAdminOrPlanner,
  });

  const queryClient = useQueryClient();

  const productsQuery = useQuery({
    queryKey: ['fetch-blocked-products', filters],
    queryFn: () => fetchBlockedProducts({ filters }),
    refetchOnWindowFocus: false,
  });

  const updateProductMutation = useMutation({
    mutationFn: updateProduct,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-blocked-products']);
      notify('success', 'Produto atualizado com sucesso!');
    },
    onError: () => {
      notify('error', 'Erro ao atualizar produto.');
    },
    onSettled: () => {
      updateProductReasonDrawerRef.current?.close();
    },
  });

  const createRedirectedProductsMutation = useMutation({
    mutationFn: createRedirectedProducts,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-blocked-products']);
      notify('success', 'Produtos redirecionados com sucesso!');
      if (NAVIGATE_TO_REDIRECTED_PRODUCTS_PAGE_ON_SUCCESS) {
        navigate('/inspecao-estoque/produtos-redirecionados');
      }
    },
    onError: () => {
      notify('error', 'Erro ao redirecionar produtos.');
    },
    onSettled: () => {
      createRedirectedProductsDrawerRef.current?.close();
      dataTableRef.current?.clearSelection();
    },
  });

  const createPrioritizedProductsMutation = useMutation({
    mutationFn: createPrioritizedProducts,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-blocked-products']);
      notify('success', 'Produtos priorizados com sucesso!');
      if (NAVIGATE_TO_PRIORITIZED_PRODUCTS_PAGE_ON_SUCCESS) {
        navigate('/inspecao-estoque/produtos-priorizados');
      }
    },
    onError: () => {
      notify('error', 'Erro ao priorizar produtos.');
    },
    onSettled: () => {
      createPrioritizedProductsDrawerRef.current?.close();
      dataTableRef.current?.clearSelection();
    },
  });

  return (
    <Scaffold>
      <TabbedNavigation />
      <PageHeaderForFilters headerTitle={
        <div className={'w-full font-GilroySemibold'}>Filtro</div>
      } className={'mb-4'}>
        <BlockedProductsFilter
          onSubmitFilters={updateFilters}
          onResetFilters={clearState}
        />
      </PageHeaderForFilters>
      <Indicators
        indicators={productsQuery?.data?.indicators}
        loading={productsQuery.isLoading}
        className={'mb-4'}
      />
      <UpdateProductReasonDrawer
        ref={updateProductReasonDrawerRef}
        onSubmit={updateProductMutation.mutate}
        loading={updateProductMutation.isLoading}
      />
      <CreatePrioritizedProductsDrawer
        ref={createPrioritizedProductsDrawerRef}
        onSubmit={createPrioritizedProductsMutation.mutate}
        loading={createPrioritizedProductsMutation.isLoading}
      />
      <CreateRedirectedProductsDrawer
        ref={createRedirectedProductsDrawerRef}
        onSubmit={createRedirectedProductsMutation.mutate}
        loading={createRedirectedProductsMutation.isLoading}
      />
      <div ref={datatableWrapperRef}>
        <DataTable
          ref={dataTableRef}
          data={productsQuery?.data?.blockedProducts ?? []}
          isLoading={productsQuery.isLoading}
          columns={columns}
          tableWrapperStyle={datatableWrapperStyle}
          leftPinnedColumns={['Selection', 'Planta']}
          rightPinnedColumns={['Actions']}
          renderTopBar={({ selectedRows, totalRows }) => {
            if (!isAdminOrPlanner) return null;

            return (
              <DataTableDropdownMenu
                selectedProducts={selectedRows.flatRows.map(i => i.original)}
                totalProducts={totalRows}
                onCreatePrioritizedProducts={createPrioritizedProductsDrawerRef.current?.open}
                onCreateRedirectedProducts={createRedirectedProductsDrawerRef.current?.open}
              />
            );
          }}
        />
      </div>
    </Scaffold>
  );
};

